import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { Tag as DefaultTag } from 'primereact/tag';
import { useQuery } from 'react-query';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Messages } from 'primereact/messages';
import { getAllRoles } from '../../../services/acl';
import { PageTitle } from '../../Headings';
import { ActionsWrapper } from '../../DataTable/common';
import { TitleWrapper } from '../../Headings/common';
import AddRoleModal from '../AddRoleModal';
import ModalDeleteFooter from '../../Modal/modalDeleteFooter';
import { useDeleteRole } from '../../../services/mutations';
import UsersPreview from './usersPreview';
import { Place, Role } from '../../../services/model';

const ConfirmationModalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const Tag = styled(DefaultTag)`
  margin-right: 6px;
  margin-bottom: 2px;
`;

const CustomLink = styled.span`
  color: ${({ theme }) => theme.colors.information};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.information_bg};
  }
`;
function RoleList() {
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Role | null>(null);
  const [deleteItem, setDeleteItem] = useState<Role | null>(null);
  const { isFetching, error, data } = useQuery('roleList', getAllRoles);
  const resultToast = useRef<Toast>(null);
  const messages = useRef<Messages>(null);
  const deleteRole = useDeleteRole();

  const actionsBodyTemplate = (rowData: Role) => (
    <ActionsWrapper>
      {/* <Button icon="pi pi-pencil" text severity="secondary" aria-label="Edit" disabled /> */}
      <Button icon="pi pi-trash" severity="secondary" text aria-label="Delete" onClick={() => setDeleteItem(rowData)} />
    </ActionsWrapper>
  );

  const handleDelete = () => {
    deleteRole.mutate(deleteItem.id, {
      onSuccess: () => {
        setDeleteItem(null);
        resultToast.current?.show({
          severity: 'success',
          summary: 'Delete role',
          detail: 'Role was successfully deleted.',
          life: 3000,
        });
      },
      onError: () =>
        resultToast.current?.show({
          severity: 'error',
          summary: 'Delete role',
          detail: 'An error has occurred while deleting role.',
          life: 3000,
        }),
    });
  };

  const renderPlaceList = (values: Place[]) => {
    if (values.length === 0) {
      return <span>-</span>;
    }

    return values.map((item: Place) => <Tag key={item.code} value={item.name} />);
  };

  const renderUsersCell = (row: Role) => {
    if (row.users?.length) {
      return (
        <>
          <span>{row.users?.length}</span>&nbsp;(
          <CustomLink onClick={() => setSelectedItem(row)}>show users</CustomLink>)
        </>
      );
    }

    return <span>-</span>;
  };

  useEffect(() => {
    if (error) {
      messages.current?.show({ severity: 'error', summary: 'An error has occurred while loading data!', sticky: true });
    }
  }, [error]);

  return (
    <>
      <Toast ref={resultToast} />
      <Dialog
        visible={!!deleteItem}
        style={{ width: '450px' }}
        header="Delete role"
        modal
        footer={<ModalDeleteFooter onCancel={() => setDeleteItem(null)} onConfirm={handleDelete} />}
        onHide={() => setDeleteItem(null)}
      >
        <ConfirmationModalWrapper>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem' }} />
          {deleteItem && (
            <span>
              Do you really want to delete role <strong>{deleteItem.name}</strong>?
            </span>
          )}
        </ConfirmationModalWrapper>
      </Dialog>
      <AddRoleModal open={showAddModal} onClose={() => setShowAddModal(false)} />
      <TitleWrapper>
        <PageTitle>Role List</PageTitle>
        <UsersPreview isOpen={!!selectedItem} items={selectedItem?.users || []} onHide={() => setSelectedItem(null)} />
        <div>
          <Button label="Role" icon="pi pi-plus" size="small" onClick={() => setShowAddModal(true)} />
        </div>
      </TitleWrapper>
      <Messages ref={messages} />
      <DataTable loading={isFetching} value={data} rows={15} size="small" rowHover paginator>
        <Column header="Name" field="name" sortable />
        <Column header="Grant" field="grant.name" sortable />
        <Column header="Places" field="placeId" body={(rowData: Role) => renderPlaceList(rowData.places)} />
        <Column header="Users" field="users" body={(rowData: Role) => renderUsersCell(rowData)} />
        <Column header="" body={actionsBodyTemplate} />
      </DataTable>
    </>
  );
}

export default RoleList;
